import { useEffect, useState } from "react";

import { useDebounce } from "@/hooks/useDebounce";
import { checkCscLogin } from "@/services/api";
import { CheckCscLoginResponseCheckCscLoginStatus } from "@/services/model";
import { createReferenceId } from "@/utils/initializeSentry";

type CustomerCheckStatus = "idle" | "checking" | "exists" | "new";

/**
 * Hook to check if a customer already exists by email address.
 * Uses the checkCscLogin endpoint with a debounced email input.
 *
 * @param email - The email address to check
 * @returns status of the customer check
 */
export const useCheckPartnerCustomer = (email: string) => {
  const [status, setStatus] = useState<CustomerCheckStatus>("idle");
  const [debouncedEmail] = useDebounce(email, 500);

  useEffect(() => {
    if (!debouncedEmail || !debouncedEmail.includes("@")) {
      setStatus("idle");

      return;
    }

    setStatus("checking");

    const referenceId = createReferenceId();

    checkCscLogin(debouncedEmail, {
      headers: {
        "X-Reference-Id": referenceId,
      },
    })
      .then((res) => {
        if (
          res.checkCscLoginStatus ===
          CheckCscLoginResponseCheckCscLoginStatus.login_exists
        ) {
          setStatus("exists");
        } else if (
          res.checkCscLoginStatus ===
          CheckCscLoginResponseCheckCscLoginStatus.login_available
        ) {
          setStatus("new");
        } else {
          setStatus("idle");
        }
      })
      .catch(() => {
        setStatus("idle");
      });
  }, [debouncedEmail]);

  return {
    status,
    isExistingCustomer: status === "exists",
    isNewCustomer: status === "new",
    isChecking: status === "checking",
  };
};
